import {ProductLabels} from 'modules/products/components/ProductLabels';
import {IMedia, IProductSection, IProductShort} from 'modules/products/types';
import React, {FC, memo, useMemo} from 'react';
import style from './style.module.scss';
import {ProductImageSwitcher} from 'modules/products/components/ProductImageSwitcher';
import {Typography} from 'components/Typography';
import Link from 'components/CustomRoutingLink';
import {Button} from 'components/Button';
import {AvailableType} from 'models';
import {ListingCardRating, SimpleWineRating} from 'modules/products/components/ListingCardRating';
import {ProductProperties} from 'modules/products/components/ProductProperties';
import {useProductClickAnalytics} from 'modules/products/hooks/gtag/useProductClickAnalytics';
import {ProductArticle} from 'modules/products/components/ProductArticle';
import {ListVisualProductCardArt} from 'modules/products/components/ListVisualProductCardArt';
import {isArtProduct} from 'modules/products/helpers';
import {ListVisualProductCardInStockLabel} from 'modules/products/components/ListVisualProductCardInStockLabel';
import classNames from 'classnames';
import {getProductFineAndRarePropValue, getProductSectionCodes} from 'modules/product-card/helpers';
import {MANUFACTURER, NET_VOLUME} from 'modules/product-card/constants/characteristics';
import {SECTION_CODE} from 'modules/product-card/constants';
import {ListVisualProductCardFnR} from 'modules/products/components/ListVisualProductCardFnR';
import {IAnalyticsMeta} from 'core/types';

interface IProps extends IProductShort {
    images?: IMedia['images'];
    isInStock?: boolean;
    availableType?: AvailableType;
    favouriteButton?: React.ReactNode;
    actions?: React.ReactNode;
    titleNode: React.ReactNode;
    topSection?: string;
    canonSection?: IProductSection;
    analytics?: IAnalyticsMeta;
}

export const ListVisualProductCard: FC<IProps> = memo(
    // eslint-disable-next-line complexity
    ({
        images = [],
        id,
        titleNode,
        availableType,
        isInStock,
        favouriteButton,
        title,
        actions,
        offerId,
        topSection,
        canonSection,
        analytics,
        ...product
    }) => {
        const {canBuy, canReserve, url, ratings = [], reviewsCount, properties, labels = [], sections = []} = product;

        const productClickAnalytics = useProductClickAnalytics();

        const isArt = isArtProduct(properties);
        const isFineAndRareCategory = useMemo(() => {
            return getProductFineAndRarePropValue(properties);
        }, [properties]);

        const isOutOfStock = !canBuy && !canReserve;

        const filteredProperties = useMemo(() => {
            const sectionCodes = getProductSectionCodes(sections);

            if (sectionCodes.includes(SECTION_CODE.STEKLO)) {
                return properties.filter(({code}) => {
                    return code === MANUFACTURER.KEY || code === NET_VOLUME.KEY;
                });
            }

            return properties;
        }, [properties, sections]);

        return (
            <div className={classNames(style.list, isFineAndRareCategory ? style.isFineAndRare : '')}>
                <div className={style.listImages} data-skeleton-item={true}>
                    {!isFineAndRareCategory && (
                        <SimpleWineRating
                            id={id}
                            analytics={analytics}
                            ratings={ratings}
                            reviewsCount={reviewsCount}
                            url={url}
                        />
                    )}
                    <Link className={style.link} href={url} onClick={productClickAnalytics(product, analytics)}>
                        <ProductImageSwitcher className={style.images} imageList={images} isDotPagination={true} />
                    </Link>
                    {(isFineAndRareCategory || isArt) && (
                        <div className={style.imageBottomArea}>
                            {isFineAndRareCategory && (
                                <div className={style.imageBottomBlock}>
                                    <ListVisualProductCardFnR />
                                </div>
                            )}
                            {isArt && (
                                <div className={style.imageBottomBlock}>
                                    <ListVisualProductCardArt />
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className={style.listInfo} data-skeleton-item={true}>
                    <div className={style.listTop}>
                        <ProductArticle article={id} />
                        {favouriteButton}
                    </div>
                    <Link href={url} onClick={productClickAnalytics({...product}, analytics)}>
                        {titleNode}
                    </Link>
                    {Boolean(labels?.length) && (
                        <div className={style.labels}>
                            <ProductLabels labels={labels} isOutOfStock={isOutOfStock} id={id} analytics={analytics} />
                        </div>
                    )}
                    {!isFineAndRareCategory && Boolean(ratings.length) && (
                        <div className={style.ratingsList}>
                            <ListingCardRating
                                id={id}
                                analytics={analytics}
                                isListType={true}
                                ratings={ratings}
                                reviewsCount={reviewsCount}
                                url={url}
                            />
                        </div>
                    )}
                    <ul className={style.listDescription}>
                        <ProductProperties
                            id={id}
                            analytics={analytics}
                            isListType={true}
                            properties={filteredProperties}
                            url={`/catalog/${topSection}`}
                            topSection={topSection}
                            sections={sections}
                            canonSection={canonSection}
                        />
                    </ul>
                </div>
                <div className={style.rightBlock}>
                    <div className={style.listActions}>
                        <div className={style.actions}>{actions}</div>
                        <div className={style.inStockLabel} data-skeleton-item={true}>
                            <ListVisualProductCardInStockLabel isInStock={isInStock} />
                        </div>
                    </div>
                    {availableType === AvailableType.OfflineWish && (
                        <div className={style.additionalInfo}>
                            <Typography variant={'p-medium'}>Этот товар можно забрать только в винотеке.</Typography>
                            <Button className={style.additinalInfoButton} size={'medium'} theme={'ghost'}>
                                Сообщить о поступлении в интернет-витрину.
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        );
    },
);
